<template>
  <PVPopover
    ref="popoverRef"
    :dismissable="isDismissable"
    :append-to="appendToTarget"
    :base-z-index="POPOVER_Z_INDEX"
    :pt="passThrough"
    @show="onPopoverShow"
    @hide="onPopoverHide"
  >
    <!-- IMPORTANT: keep font-weight-500 for popover title bold until further tokens are created-->
    <div
      v-if="hasTitle"
      class="mb-1 font-weight-500"
    >
      <slot name="title" />
    </div>
    <slot>{{ content }}</slot>
  </PVPopover>
</template>
<script lang="ts">
/**
 * PrimeVue-backed popover styled via the active design-token preset (see /design playground).
 * Keeps BootstrapVue-compatible trigger/target/placement API on top of styled PVPopover.
 */
export default {
  name: 'StandardPopover',
  compatConfig: { MODE: 3 },
  inheritAttrs: false,
};
</script>
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, useAttrs, watch } from 'vue';
import { hasRenderedSlotContent } from '@/utils/vueSlots';
import { POPOVER_Z_INDEX } from '@/constants/ComponentZIndices';
import {
  type DelayConfig,
  type Placement,
  type Trigger,
  VIEWPORT_EDGE_INSET_PX,
  computeAlignedPosition,
  getTargetBoxCenter,
  parseDelay,
  parsePlacement,
  parseTriggers,
  resolvePopoverGutterPx,
  resolveTarget,
  setPrimeVueArrowOffset,
  setPrimeVueArrowState,
  setSideArrowOffset,
  shiftPopoverHorizontalForArrow,
  shiftPopoverVerticalForArrow,
} from './standardPopoverUtils';

/**
 * IMPORTANT: The target element for the popover must exist in the document before
 * <StandardPopover> is mounted. If the target element is not found during mount,
 * the popover will never open. Always place your <StandardPopover> component lower
 * in the DOM than your target element.
 */

const props = withDefaults(
  defineProps<{
    content?: string;
    target?: string;

    /**
     * The triggers prop is used to determine how the popover should be triggered.
     * It is an array of triggers or a string of space-separated triggers.
     * The default trigger is 'click'.
     */
    triggers?: Trigger | Trigger[] | string;
    placement?: Placement;
    delay?: DelayConfig;
    disabled?: boolean;
    /**
     * Dismiss the popover on outside click. Defaults to true.
     * Hover and focus triggers are always non-dismissable.
     */
    dismissable?: boolean;
    /**
     * The show prop is used to manually show the popover.
     * It is only observed for manual triggers and when the show prop is defined.
     */
    show?: boolean;
    container?: HTMLElement | string;
    dataTestid?: string;
  }>(),
  {
    placement: 'right',
    dismissable: true,
  },
);

const emit = defineEmits<{
  (e: 'show'): void;
  (e: 'shown'): void;
  (e: 'hide'): void;
  (e: 'hidden'): void;
}>();

const slots = useSlots();

const attrs = useAttrs();

const popoverRef = ref<{
  show: (event: Event, target?: HTMLElement) => void;
  hide: () => void;
  toggle: (event: Event, target?: HTMLElement) => void;
  container?: HTMLElement;
} | null>(null);

const hasTitle = computed((): boolean => Boolean(slots.title));

/**
 * Whether the popover has any visible content to display. BootstrapVue's
 * b-popover suppressed the popover entirely when both title and content
 * were empty; PrimeVue's PVPopover renders an empty box instead. We restore
 * that suppression by treating an empty popover as effectively disabled.
 * hasTitle is intentionally left unchanged so header rendering is not
 * affected; the title slot is checked for rendered content here instead.
 */
const hasContent = computed((): boolean => {
  if (props.content !== undefined && props.content !== null && String(props.content).trim() !== '') {
    return true;
  }

  if (slots.title !== undefined && hasRenderedSlotContent(slots.title())) {
    return true;
  }

  return slots.default !== undefined && hasRenderedSlotContent(slots.default());
});

const isEffectivelyDisabled = computed((): boolean => props.disabled || !hasContent.value);

const triggerList = computed((): Trigger[] => parseTriggers(props.triggers));
const delayMs = computed((): { show: number; hide: number } => parseDelay(props.delay));

const isHoverTrigger = computed((): boolean => triggerList.value.includes('hover'));
const isClickTrigger = computed((): boolean => triggerList.value.includes('click'));
const isFocusTrigger = computed((): boolean => triggerList.value.includes('focus'));
const isManualTrigger = computed((): boolean => triggerList.value.includes('manual'));

const isDismissable = computed((): boolean => !isHoverTrigger.value && !isFocusTrigger.value && props.dismissable);

const parsedPlacement = computed(() => parsePlacement(props.placement));

const placementModifierClass = computed((): string => {
  const { base } = parsedPlacement.value;

  return `standard-popover--arrow-${base}`;
});

const rootClass = computed((): string => {
  const classes = ['standard-popover', placementModifierClass.value, attrs.class].filter(Boolean).join(' ');

  return classes;
});

const passThrough = computed(() => ({
  root: {
    class: rootClass.value,
    'data-testid': props.dataTestid,
  },
}));

const appendToTarget = computed((): HTMLElement | 'body' => {
  if (!props.container) {
    return 'body';
  }

  if (typeof props.container === 'string') {
    return document.getElementById(props.container.replace(/^#/, '')) ?? 'body';
  }

  return props.container;
});

let targetElement: HTMLElement | null = null;
let popoverElement: HTMLElement | null = null;
let showTimeout: ReturnType<typeof setTimeout> | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;
let popoverPositionObserver: ResizeObserver | null = null;

const isNodeInsideHoverZone = (node: EventTarget | null): boolean => {
  if (!(node instanceof Node)) {
    return false;
  }

  const overlay = popoverRef.value?.container ?? popoverElement;

  return Boolean(
    (targetElement && (node === targetElement || targetElement.contains(node))) ||
    (overlay && (node === overlay || overlay.contains(node))),
  );
};

/**
 * Positions the popover on the target. This is called when the popover is shown or resized.
 * It is used to ensure the popover is positioned correctly on the target.
 */
const positionPopoverOnTarget = async (): Promise<void> => {
  const popover = popoverRef.value?.container ?? null;

  if (!popover) {
    return;
  }

  await refreshTarget(true);

  if (!targetElement) {
    return;
  }

  const targetRect = targetElement.getBoundingClientRect();
  const targetAnchor = getTargetBoxCenter(targetElement);
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft || 0;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
  const { base, alignment } = parsedPlacement.value;
  const popoverGutterPx = resolvePopoverGutterPx(popover);

  const popoverHeight = popover.offsetHeight;
  const popoverWidth = popover.offsetWidth;

  // PVPopover.alignOverlay() positions below the target; we own placement for all sides.
  popover.style.marginTop = '0';
  popover.style.marginBlockStart = '0';
  popover.style.marginBlockEnd = '0';
  popover.style.transformOrigin = '';
  popover.style.insetInlineEnd = '';

  setPrimeVueArrowState(popover, base);

  // Left/right: pin to the target's side (main axis + gutter).
  // Vertical alignment is the cross axis, computed in viewport space then shifted by
  // scrollTop because `style.top` is document-relative.
  if (base === 'left' || base === 'right') {
    const minTopViewport = VIEWPORT_EDGE_INSET_PX;
    const maxTopViewport = window.innerHeight - popoverHeight - VIEWPORT_EDGE_INSET_PX;
    const initialPopoverTopViewport = computeAlignedPosition(
      alignment,
      targetRect.top,
      targetRect.height,
      popoverHeight,
      0,
      minTopViewport,
      maxTopViewport,
    );
    const shiftedPopoverTopViewport = shiftPopoverVerticalForArrow(
      initialPopoverTopViewport,
      targetAnchor.y,
      popoverHeight,
      popoverGutterPx,
      minTopViewport,
      maxTopViewport,
    );

    popover.style.top = `${shiftedPopoverTopViewport + scrollTop}px`;
    popover.style.insetInlineStart =
      base === 'right'
        ? `${targetRect.right + scrollLeft + popoverGutterPx}px`
        : `${targetRect.left + scrollLeft - popoverWidth - popoverGutterPx}px`;

    void popover.offsetHeight;

    const paintedTop = popover.getBoundingClientRect().top;
    // `style.top` is vs BODY (offset parent), not the window. First write pretends they
    // are the same, so paint lands below the planned viewport Y by this amount (gutter /
    // containing block; ~2px Discover, ~9px screener). Subtract it from `style.top` so
    // paint matches the plan; pass it into the side arrow (viewport Y minus painted top).
    const viewportDeltaPx = paintedTop - shiftedPopoverTopViewport;

    if (viewportDeltaPx !== 0) {
      popover.style.top = `${shiftedPopoverTopViewport + scrollTop - viewportDeltaPx}px`;
      void popover.offsetHeight;
    }

    setSideArrowOffset(popover, targetAnchor.y, viewportDeltaPx);
    return;
  }

  // Top/bottom: pin above or below the target. Horizontal alignment is the
  // cross axis, clamped so the overlay stays inside the viewport.
  const minLeftViewport = VIEWPORT_EDGE_INSET_PX;
  const maxLeftViewport = window.innerWidth - popoverWidth - VIEWPORT_EDGE_INSET_PX;
  const initialPopoverLeftViewport = computeAlignedPosition(
    alignment,
    targetRect.left,
    targetRect.width,
    popoverWidth,
    0,
    minLeftViewport,
    maxLeftViewport,
  );
  const shiftedPopoverLeftViewport = shiftPopoverHorizontalForArrow(
    initialPopoverLeftViewport,
    targetAnchor.x,
    popoverWidth,
    popoverGutterPx,
    minLeftViewport,
    maxLeftViewport,
  );

  popover.style.insetInlineStart = `${shiftedPopoverLeftViewport + scrollLeft}px`;

  if (base === 'top') {
    popover.style.top = `${targetRect.top + scrollTop - popoverHeight - popoverGutterPx}px`;
  } else {
    // Own the gutter in `top` because we zeroed margin-block-start (PrimeVue's default
    // top/bottom arrow lives in that margin).
    popover.style.top = `${targetRect.bottom + scrollTop + popoverGutterPx}px`;
  }

  void popover.offsetWidth;
  setPrimeVueArrowOffset(popover, targetAnchor.x);
};

const bindPopoverPositionObserver = (): void => {
  // unbind any existing observer
  unbindPopoverPositionObserver();

  if (!popoverRef.value?.container) {
    return;
  }

  // PVPopover's own contentResizeObserver (registered in onEnter just before $emit('show'))
  // calls alignOverlay() on every resize, which overwrites our inset-inline-start.
  // Both observers targeting the same element creates a feedback loop when near the viewport
  // edge. We own all positioning, so disconnect PVPopover's observer.
  (popoverRef.value as { unbindContentResizeListener?: () => void } | null)?.unbindContentResizeListener?.();

  popoverPositionObserver = new ResizeObserver(() => {
    void positionPopoverOnTarget();
  });

  popoverPositionObserver.observe(popoverRef.value.container);
};

/**
 * Unbinds the popover position observer.
 */
const unbindPopoverPositionObserver = (): void => {
  if (!popoverPositionObserver) {
    return;
  }

  popoverPositionObserver.disconnect();
  popoverPositionObserver = null;
};

/**
 * Schedules the position of the popover on the target.
 */
const schedulePositionPopover = (): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void positionPopoverOnTarget();
    });
  });
};

const clearShowTimeout = (): void => {
  if (showTimeout === null) {
    return;
  }

  clearTimeout(showTimeout);
  showTimeout = null;
};

const clearHideTimeout = (): void => {
  if (hideTimeout === null) {
    return;
  }

  clearTimeout(hideTimeout);
  hideTimeout = null;
};

/**
 * Shows the popover.
 */
const showPopover = async (_event: Event): Promise<void> => {
  await refreshTarget(true);

  if (isEffectivelyDisabled.value || !targetElement) {
    return;
  }

  clearHideTimeout();

  // Set selfClick=true before calling show() so PVPopover's outside-click listener
  // absorbs the triggering click (which may still be bubbling to document) rather than
  // immediately closing the just-opened popover. PVPopover uses the same selfClick flag
  // in onContentClick() to swallow clicks inside the popover body.
  //
  // Also pass targetElement as currentTarget so PVPopover stores the correct eventTarget,
  // which isTargetClicked() uses to guard against repeated clicks on the target element.
  if (popoverRef.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accessing PVPopover internal state
    (popoverRef.value as any).selfClick = true;
  }
  popoverRef.value?.show({ currentTarget: targetElement } as unknown as Event, targetElement);
};

/**
 * Hides the popover.
 */
const hidePopover = (): void => {
  clearShowTimeout();
  popoverRef.value?.hide();
};

const scheduleShowPopover = (event: Event): void => {
  clearShowTimeout();
  clearHideTimeout();

  showTimeout = setTimeout(() => {
    void showPopover(event);
  }, delayMs.value.show);
};

const scheduleHidePopover = (): void => {
  clearShowTimeout();
  clearHideTimeout();

  hideTimeout = setTimeout(() => {
    hidePopover();
  }, delayMs.value.hide);
};

/*
 * Cancel a pending hide when the pointer reaches the overlay. Used for hover
 * triggers and for manual + show=false (parent already dismissed; hide delay
 * is in flight so the card stays clickable across the target → overlay gap).
 */
const onPopoverMouseEnter = (): void => {
  clearHideTimeout();
};

const onPopoverMouseLeave = (event: MouseEvent): void => {
  const shouldHideFromOverlayLeave = isHoverTrigger.value || (isManualTrigger.value && props.show === false);

  if (!shouldHideFromOverlayLeave) {
    return;
  }

  if (isNodeInsideHoverZone(event.relatedTarget)) {
    return;
  }

  scheduleHidePopover();
};

const bindPopoverElementListeners = (): void => {
  unbindPopoverElementListeners();

  popoverElement = popoverRef.value?.container ?? null;

  if (!popoverElement) {
    return;
  }

  popoverElement.addEventListener('mouseenter', onPopoverMouseEnter);
  popoverElement.addEventListener('mouseleave', onPopoverMouseLeave);
};

const unbindPopoverElementListeners = (): void => {
  if (!popoverElement) {
    return;
  }

  popoverElement.removeEventListener('mouseenter', onPopoverMouseEnter);
  popoverElement.removeEventListener('mouseleave', onPopoverMouseLeave);
  popoverElement = null;
};

const onTargetMouseEnter = (event: MouseEvent): void => {
  scheduleShowPopover(event);
};

const onTargetMouseLeave = (event: MouseEvent): void => {
  if (isNodeInsideHoverZone(event.relatedTarget)) {
    return;
  }

  scheduleHidePopover();
};

const onTargetClick = (event: MouseEvent): void => {
  if (!targetElement) {
    return;
  }

  if (popoverRef.value) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accessing PVPopover internal state
    (popoverRef.value as any).selfClick = true;
  }

  popoverRef.value?.toggle(event, targetElement);
};

const onTargetFocusIn = (event: FocusEvent): void => {
  scheduleShowPopover(event);
};

const onTargetFocusOut = (): void => {
  scheduleHidePopover();
};

const onPopoverShow = (): void => {
  emit('show');

  // Stop PVPopover from re-running alignOverlay() on content resize; we own positioning.
  (popoverRef.value as { unbindContentResizeListener?: () => void } | null)?.unbindContentResizeListener?.();

  void nextTick(() => {
    schedulePositionPopover();
    bindPopoverPositionObserver();
    bindPopoverElementListeners();

    emit('shown');
  });
};

const onPopoverHide = (): void => {
  unbindPopoverPositionObserver();
  unbindPopoverElementListeners();

  emit('hide');

  void nextTick(() => {
    emit('hidden');
  });
};

const unbindTargetListeners = (): void => {
  if (!targetElement) {
    return;
  }

  targetElement.removeEventListener('mouseenter', onTargetMouseEnter);
  targetElement.removeEventListener('mouseleave', onTargetMouseLeave);
  targetElement.removeEventListener('click', onTargetClick);
  targetElement.removeEventListener('focusin', onTargetFocusIn);
  targetElement.removeEventListener('focusout', onTargetFocusOut);
};

const bindTargetListeners = (): void => {
  if (!targetElement || isEffectivelyDisabled.value) {
    return;
  }

  if (isHoverTrigger.value) {
    targetElement.addEventListener('mouseenter', onTargetMouseEnter);
    targetElement.addEventListener('mouseleave', onTargetMouseLeave);
  }

  if (isClickTrigger.value) {
    targetElement.addEventListener('click', onTargetClick);
  }

  if (isFocusTrigger.value) {
    targetElement.addEventListener('focusin', onTargetFocusIn);
    targetElement.addEventListener('focusout', onTargetFocusOut);
  }
};

/**
 * Re-resolves the target element from the DOM and rebinds its event listeners.
 *
 * Always waits for nextTick so that any pending Vue DOM updates complete before
 * querying. Pass onlyIfStale=true to skip the work entirely when the cached
 * element is still connected — avoids unbinding/rebinding listeners on every
 * resize event (important for hover triggers that need continuous listeners).
 */
const refreshTarget = async (onlyIfStale = false): Promise<void> => {
  if (onlyIfStale && targetElement && document.contains(targetElement)) {
    return;
  }

  unbindTargetListeners();
  await nextTick();
  targetElement = resolveTarget(props.target);
  bindTargetListeners();
};

// eslint-disable-next-line no-restricted-syntax -- shallow watch on container prop identity
watch(
  () => props.container,
  () => {
    hidePopover();
  },
);

// eslint-disable-next-line no-restricted-syntax -- shallow watch on target prop identity
watch(
  () => props.target,
  async () => {
    await refreshTarget();
    schedulePositionPopover();
  },
);

// eslint-disable-next-line no-restricted-syntax -- boolean prop, no deep watch needed
watch(isEffectivelyDisabled, (isDisabled) => {
  if (isDisabled) {
    hidePopover();
    unbindTargetListeners();
    return;
  }

  bindTargetListeners();
});

// eslint-disable-next-line no-restricted-syntax -- boolean prop, no deep watch needed
watch(
  () => props.show,
  (latestShowValue) => {
    // only observe this for manual triggers and when the show prop is defined.
    if (!isManualTrigger.value || latestShowValue === undefined) {
      return;
    }

    /**
     * If the popover should be shown, show it.
     */
    if (latestShowValue) {
      scheduleShowPopover(new Event('manual'));
      return;
    }

    // Hide the popover if it should be hidden.
    scheduleHidePopover();
  },
);

onMounted(() => {
  void refreshTarget();

  if (isManualTrigger.value && props.show) {
    scheduleShowPopover(new Event('manual'));
  }
});

onBeforeUnmount(() => {
  clearShowTimeout();
  clearHideTimeout();
  unbindTargetListeners();
  unbindPopoverElementListeners();
  unbindPopoverPositionObserver();
});

defineExpose({
  show: (event?: Event) => {
    showPopover(event ?? new Event('manual'));
  },
  hide: hidePopover,
});
</script>

<style lang="scss">
// Unscoped on purpose: PVPopover portals to <body>, so the overlay is not under this component's DOM.
//
// Technically, we want to use scoped `:global()`, but it doesn't work with nested rules(vue scoped compiler error).
// reference: https://github.com/vuejs/core/issues/10403
//
// If we want to overwrite standard-popover's max-width, we can use the style in parent component:
// :global(.p-popover.standard-popover.popover-universe-table) {
//   max-width: 1000px;
// }

$popover-max-width: 300px;

// Matches VIEWPORT_EDGE_INSET_PX so tall content stays on-screen and scrolls.
$standard-popover-viewport-inset: 8px;

// PrimeVue `.p-popover:after { border-width: calc(gutter - 2px) }` so the fill triangle
// is similar (same angle) and concentric with the larger border triangle.
$popover-arrow-fill-inset: 2px;

.p-popover.standard-popover {
  max-width: $popover-max-width;
  word-wrap: break-word;

  .p-popover-content {
    min-width: 0;
    max-height: calc(100vh - #{$standard-popover-viewport-inset * 2});
    overflow-x: hidden;
    overflow-y: auto;
  }

  // Top/bottom: JS writes --p-popover-arrow-left as the triangle center. Drop PrimeVue's
  // arrow.offset on `left`, but keep the different negative margins so ::before (border)
  // and ::after (fill) stay concentric — same angle, fill is 2px smaller.
  &--arrow-top,
  &--arrow-bottom {
    &::before,
    &::after {
      left: var(--p-popover-arrow-left) !important;
    }

    &::before {
      margin-left: calc(-1 * var(--p-popover-gutter)) !important;
    }

    &::after {
      margin-left: calc(-1 * (var(--p-popover-gutter) - #{$popover-arrow-fill-inset})) !important;
    }
  }

  // Left/right placement: PrimeVue has no side-arrow tokens. Same equal-gutter
  // similar triangles as top/bottom, rotated 90°.
  &--arrow-left,
  &--arrow-right {
    margin-top: 0;
    margin-block-start: 0;
    margin-block-end: 0;

    // JS writes --standard-popover-arrow-top as the triangle center (PrimeVue has no side-arrow
    // token). Drop PrimeVue top/bottom `bottom` + `margin-left`, then render a transparent
    // equal-gutter triangle; left/right blocks color one side so it points at the target.
    &::before,
    &::after {
      bottom: auto;
      top: var(--standard-popover-arrow-top, 50%) !important;
      margin-left: 0;
      border-style: solid;
      border-color: transparent;
    }

    // ::before = border triangle. Negative margin-top centers it on `top` (same as
    // margin-left on top/bottom arrows).
    &::before {
      margin-top: calc(-1 * var(--p-popover-gutter));
      border-width: var(--p-popover-gutter);
    }

    // ::after = fill triangle, 2px smaller, concentric with ::before.
    &::after {
      margin-top: calc(-1 * (var(--p-popover-gutter) - #{$popover-arrow-fill-inset}));
      border-width: calc(var(--p-popover-gutter) - #{$popover-arrow-fill-inset});
    }
  }

  // Popover on the right of the target — arrow on the left edge, pointing left.
  &--arrow-right {
    &::before,
    &::after {
      left: auto;
      right: 100%;
    }

    &::before {
      border-right-color: var(--p-popover-border-color);
    }

    &::after {
      border-right-color: var(--p-popover-background);
    }
  }

  // Popover on the left of the target — arrow on the right edge, pointing right.
  &--arrow-left {
    &::before,
    &::after {
      left: 100%;
      right: auto;
    }

    &::before {
      border-left-color: var(--p-popover-border-color);
    }

    &::after {
      border-left-color: var(--p-popover-background);
    }
  }
}
</style>
